import {hpcAttrs} from '@github-ui/hpc'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {MarkdownLastEditedBy} from '@github-ui/markdown-edit-history-viewer/MarkdownLastEditedBy'
import {IconButton, Label, Link, RelativeTime} from '@primer/react'
import {PencilIcon} from '@primer/octicons-react'
import {useRef, useState} from 'react'
import {ExpandableContent} from './ExpandableContent'
import {DescriptionEditor} from './DescriptionEditor'
import {useDescriptionData} from '../../page-data/loaders/use-description-data'
import {useOverviewRouteData} from './data/use-overview-route-data'
import type {SafeHTMLString} from '@github-ui/safe-html'
import styles from './Description.module.css'

import {useLayoutRouteData} from '../../page-data/loaders/use-layout-route-data'
import {useLayoutCanEditTitle} from '../../stores/layout-selectors'
import {clsx} from 'clsx'

/** PR description section on the Overview tab with expandable overflow */
export function Description() {
  const {
    description: initialDescriptionHtml,
    descriptionText: initialDescriptionText,
    editInformation,
  } = useOverviewRouteData()
  const {
    pullRequest: {author, createdTime},
  } = useLayoutRouteData()
  // Editing the title has the same permissions as editing the description
  const canEditDescription = useLayoutCanEditTitle()

  const {data: descriptionData} = useDescriptionData({
    initialBodyHtml: initialDescriptionHtml ?? undefined,
    initialBodyText: initialDescriptionText ?? undefined,
    initialEditInformation: editInformation,
  })
  // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
  const description = descriptionData?.bodyHtml ? (descriptionData.bodyHtml as SafeHTMLString) : null
  const body = descriptionData?.body ?? ''

  const [isEditing, setIsEditing] = useState(false)
  const editButtonRef = useRef<HTMLButtonElement>(null)

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const copilot = isCopilot(author.login)
  const appSlug = author.slug || author.login
  const profileUrl = copilot ? `/apps/${appSlug}` : `/${author.login}`
  const hovercardAttributes = copilot
    ? getHovercardAttributesForType('copilot', {botSlug: appSlug})
    : getHovercardAttributesForType('user', {login: author.login})
  const avatar = copilot ? (
    <CopilotAvatar size="medium" isDecorative />
  ) : author.avatarUrl ? (
    <GitHubAvatar src={author.avatarUrl} size={24} />
  ) : null

  return (
    <section className={styles.container} aria-label="Description" {...hpcAttrs()}>
      <div className={styles.header}>
        {avatar ? (
          <Link href={profileUrl} {...hovercardAttributes} aria-label={`${author.login}'s profile`}>
            {avatar}
          </Link>
        ) : null}
        <span className="d-flex flex-row gap-1 flex-items-center">
          <Link href={profileUrl} {...hovercardAttributes} className="fgColor-default">
            <strong>{author.login}</strong>
          </Link>
          {copilot && (
            <Label variant="secondary" className="ml-1">
              AI
            </Label>
          )}
          <span className="fgColor-muted d-flex flex-row gap-1 flex-items-center">
            opened
            {createdTime && <RelativeTime date={new Date(createdTime)} />}
          </span>
        </span>
        <span style={{flexGrow: 1}} />
        <MarkdownLastEditedBy editInformation={descriptionData?.editInformation} />
        {canEditDescription && (
          <IconButton
            ref={editButtonRef}
            icon={PencilIcon}
            aria-label="Edit description"
            variant="invisible"
            size="small"
            className={clsx(isEditing && 'v-hidden')}
            onClick={() => setIsEditing(true)}
          />
        )}
      </div>
      <ExpandableContent
        sectionLabel="Description"
        storageKey="overview-description-expanded"
        defaultExpanded
        disabled={isEditing}
      >
        <DescriptionEditor
          bodyHtml={description}
          body={body}
          isEditing={isEditing}
          onEditingChange={setIsEditing}
          returnFocusRef={editButtonRef}
        />
      </ExpandableContent>
    </section>
  )
}
